// Story 8.10 (ADO-2019), DV-39: keep the HHT waiting count current while the Shopper browses,
// so a file uploaded since sign-in shows in the header badge without a visit to /choose
// (SL1.0 recounted on every page, `parts/nav.php:12-33`). Mounted ONCE at the composition
// root (main.tsx), inside the router, so no page wires it and no page spec is coupled to it.
//
//   - every route change: `refreshIfStale`, at most one read a minute however fast the
//     Shopper moves between pages;
//   - the tab regains focus (`focus`, or `visibilitychange` to visible): `load`, not
//     throttled by the minute. Coming back to the tab is when a new upload is most likely
//     to be expected, the read is one database count, and the two events a browser fires
//     together join the one request in flight, so a return costs one read.
//
// Signed out it does nothing. It renders nothing.

import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore } from '../auth/useAuthStore';
import { useHhtWaitingCountStore } from './useHhtWaitingCountStore';

export interface HhtWaitingCountRefresherProps {
  /** The clock, in epoch milliseconds (a spec pins it). */
  readonly now?: () => number;
}

export function HhtWaitingCountRefresher({ now = Date.now }: HhtWaitingCountRefresherProps) {
  const { pathname } = useLocation();
  const signedIn = useAuthStore((s) => s.status === 'ready' && s.isAuthenticated);

  useEffect(() => {
    if (!signedIn) return;
    void useHhtWaitingCountStore.getState().refreshIfStale(now());
  }, [pathname, signedIn, now]);

  useEffect(() => {
    if (!signedIn) return undefined;
    const reread = (): void => {
      if (document.visibilityState === 'hidden') return;
      void useHhtWaitingCountStore.getState().load(now());
    };
    window.addEventListener('focus', reread);
    document.addEventListener('visibilitychange', reread);
    return () => {
      window.removeEventListener('focus', reread);
      document.removeEventListener('visibilitychange', reread);
    };
  }, [signedIn, now]);

  return null;
}
